import React from "react"

import { Loader2 } from "lucide-react"
import { toast } from "sonner"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useSamsaraCreateHubLocation } from "@/hooks/useCollections"
import { cn } from "@/lib/utils"

import { LocationFormData } from "./types"

/** Where the single-site and bulk dialogs differ; kept as-is so sharing changed nothing on screen. */
const VARIANTS = {
  location: {
    withFieldIds: true,
    buttonLabel: "Create in location in Samsara",
    manualIdPlaceholder: "Enter Samsara address ID or create in Samsara above",
    // The bulk form hides the manual fields while a retry is in flight; this one keeps them.
    clearFailureBeforeCreate: false,
    // A fresh site starts on default coordinates, so only a Samsara id means Samsara answered.
    summaryNeedsLocationId: true,
  },
  bulk: {
    withFieldIds: false,
    buttonLabel: "Create location in Samsara",
    manualIdPlaceholder: "Enter Samsara address ID or retry Create in Samsara above",
    clearFailureBeforeCreate: true,
    summaryNeedsLocationId: false,
  },
} as const

interface SamsaraLocationFieldsProps {
  variant: keyof typeof VARIANTS
  form: Partial<LocationFormData>
  setForm: React.Dispatch<React.SetStateAction<Partial<LocationFormData>>>
  /** Samsara could not place the site, so the id and coordinates are typed in by hand. */
  samsaraFailed: boolean
  setSamsaraFailed: (failed: boolean) => void
  /** An existing site: no Samsara button, and the id and coordinates are always editable. */
  editing?: boolean
}

const SamsaraLocationFields: React.FC<SamsaraLocationFieldsProps> = ({
  variant,
  form,
  setForm,
  samsaraFailed,
  setSamsaraFailed,
  editing = false,
}) => {
  const samsaraCreateHubLocation = useSamsaraCreateHubLocation()
  const config = VARIANTS[variant]
  const fieldId = (id: string) => (config.withFieldIds ? id : undefined)

  const hasCoordinates = form.latitude != null && form.longitude != null
  const showSummary =
    !editing &&
    !samsaraFailed &&
    (config.summaryNeedsLocationId ? !!form.location_id : !!form.location_id || hasCoordinates)

  const createInSamsara = async () => {
    const address = form.address?.trim() || ""
    const postcode = form.postcode?.trim() || ""
    const formattedAddress = [address, postcode].filter(Boolean).join(", ")
    const name = form.site_name?.trim() || "Location"
    if (config.clearFailureBeforeCreate) setSamsaraFailed(false)
    try {
      const result = await samsaraCreateHubLocation.mutateAsync({
        name,
        formattedAddress,
      })
      setForm((prev) => ({
        ...prev,
        location_id: result.locationId,
        latitude: result.latitude,
        longitude: result.longitude,
      }))
      if (result.latitude == null || result.longitude == null) {
        setSamsaraFailed(true)
        toast.error("Samsara created location, but no coordinates returned", {
          description:
            "Please enter latitude/longitude manually below (or try again in a moment).",
        })
      } else {
        setSamsaraFailed(false)
        toast.success("Samsara location created", {
          description: "Location ID and coordinates filled from Samsara.",
        })
      }
    } catch (err) {
      setSamsaraFailed(true)
      toast.error("Samsara create failed", {
        description:
          err instanceof Error
            ? err.message
            : "Could not create location in Samsara. Enter Location ID and coordinates below.",
      })
    }
  }

  const requiredMark = !editing && samsaraFailed ? "*" : ""

  return (
    <>
      <div className={cn("grid gap-4 items-end", !editing && "grid-cols-[1fr_auto]")}>
        <div className="grid gap-2">
          <Label htmlFor={fieldId("postcode")}>Postcode *</Label>
          <Input
            id={fieldId("postcode")}
            value={form.postcode || ""}
            onChange={(e) => setForm((prev) => ({ ...prev, postcode: e.target.value }))}
            className="bg-white"
          />
        </div>
        {!editing && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={
              !(form.address?.trim() && form.postcode?.trim()) ||
              samsaraCreateHubLocation.isPending
            }
            onClick={createInSamsara}
          >
            {samsaraCreateHubLocation.isPending && (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            )}
            {config.buttonLabel}
          </Button>
        )}
      </div>
      {showSummary && (
        <div className="text-xs text-green-700 bg-green-50 px-2 py-1 rounded">
          <div>
            Location ID: <span className="font-medium">{form.location_id}</span>
          </div>
          {form.latitude != null && form.longitude != null ? (
            <div>
              Lat/Lon:{" "}
              <span className="font-medium">
                {form.latitude.toFixed(6)}, {form.longitude.toFixed(6)}
              </span>
            </div>
          ) : (
            <div>
              Lat/Lon: <span className="font-medium">Not returned</span>
            </div>
          )}
        </div>
      )}
      {(editing || samsaraFailed) && (
        <>
          <div className="grid gap-2">
            <Label htmlFor={fieldId("locationId")}>Location ID {requiredMark}</Label>
            <Input
              id={fieldId("locationId")}
              value={form.location_id || ""}
              onChange={(e) => setForm((prev) => ({ ...prev, location_id: e.target.value }))}
              className="bg-white"
              placeholder={samsaraFailed ? config.manualIdPlaceholder : undefined}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label htmlFor={fieldId("latitude")}>Latitude {requiredMark}</Label>
              <Input
                id={fieldId("latitude")}
                type="number"
                step="0.0001"
                value={form.latitude ?? ""}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    latitude: e.target.value === "" ? null : parseFloat(e.target.value),
                  }))
                }
                className="bg-white"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor={fieldId("longitude")}>Longitude {requiredMark}</Label>
              <Input
                id={fieldId("longitude")}
                type="number"
                step="0.0001"
                value={form.longitude ?? ""}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    longitude: e.target.value === "" ? null : parseFloat(e.target.value),
                  }))
                }
                className="bg-white"
              />
            </div>
          </div>
        </>
      )}
    </>
  )
}

export default SamsaraLocationFields
